/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { formatRunError } from "../harness/error.js";
import { timestamp } from "../timestamp.js";
function omit(message, ...keys) {
    const copy = { ...message };
    for (const key of keys) {
        delete copy[key];
    }
    return copy;
}
function noConfig(data) {
    const node = { ...data.node };
    delete node.configuration;
    return {
        ...data,
        node,
    };
}
function noIO(data) {
    const result = { ...data };
    delete result.inputs;
    delete result.outputs;
    return result;
}
export class DiagnosticsFilter {
    #writer;
    #diagnostics;
    constructor(writer, diagnostics) {
        this.#writer = writer;
        this.#diagnostics = diagnostics;
    }
    #filterTop(pathLength) {
        return this.#diagnostics === "top" && pathLength > 1;
    }
    async writeGraphStart(data) {
        if (this.#filterTop(data.path.length + 1)) {
            return;
        }
        await this.#writer.write(["graphstart", omit(data, "graph")]);
    }
    async writeGraphEnd(data) {
        if (this.#filterTop(data.path.length + 1)) {
            return;
        }
        await this.#writer.write(["graphend", data]);
    }
    async writeNodeStart(data) {
        if (this.#filterTop(data.path.length)) {
            return;
        }
        await this.#writer.write(["nodestart", noConfig(omit(data, "inputs"))]);
    }
    async writeNodeEnd(data) {
        if (this.#filterTop(data.path.length)) {
            return;
        }
        await this.#writer.write(["nodeend", noIO(noConfig(data))]);
    }
    async writeSkip(_data) {
        // Do not write skip messages to the server.
        // await this.#writer.write(["skip", data]);
    }
    async writeEdge(data) {
        if (this.#diagnostics !== true) {
            return;
        }
        await this.#writer.write(["edge", data]);
    }
    async writeInput(data, next) {
        await this.#writer.write(["input", data, next]);
    }
    async writeOutput(data) {
        await this.#writer.write(["output", data]);
    }
    async writeError(data) {
        await this.#writer.write([
            "error",
            { error: formatRunError(data.error), timestamp: timestamp() },
        ]);
    }
    async writeEnd(data) {
        if (this.#diagnostics) {
            await this.#writer.write(["end", data]);
        }
    }
}
//# sourceMappingURL=diagnostics-filter.js.map